import React, { useState, useEffect } from "react";
import useSettings from "../../../../hooks/settings";
import { Settings } from "../../../../models/settings_model";
import { IconButton } from "../../common/buttons";
import {
  getPosAlertsEnabled,
  setPosAlertsEnabled,
} from "../../../notifications/OnlineOrderAlert";
import { FIXED_PLATFORM_OPTIONS, parsePlatformsEnabled } from "../../../../middleware/platforms";

const Sales: React.FC = () => {
  const [settings, saveSettings] = useSettings();
  const [updated, setUpdated] = useState(false);

  useEffect(() => {
    return () => {
      if (updated) {
        window.location.reload();
      }
    };
  }, [updated]);

  const updateSetting = (ev: { currentTarget: HTMLButtonElement }) => {
    const setting_type = ev.currentTarget.id as keyof Settings;
    const checked = !settings[setting_type];
    saveSettings(setting_type, checked);
    setUpdated(true);
  };

  const posAddClient = settings.pos_add_client || "disabled";
  const [posAlertsOn, setPosAlertsOn] = useState(getPosAlertsEnabled);

  const enabledPlatforms = parsePlatformsEnabled(settings.platforms_enabled);
  const savedOtherPlatform =
    enabledPlatforms.find((p) => !FIXED_PLATFORM_OPTIONS.includes(p)) || "";
  const [otherPlatformOn, setOtherPlatformOn] = useState(!!savedOtherPlatform);
  const [otherPlatformInput, setOtherPlatformInput] = useState(savedOtherPlatform);

  const togglePlatform = (name: string) => {
    const next = enabledPlatforms.includes(name)
      ? enabledPlatforms.filter((p) => p !== name)
      : [...enabledPlatforms, name];
    saveSettings("platforms_enabled", JSON.stringify(next));
    setUpdated(true);
  };

  const toggleOtherPlatform = () => {
    if (otherPlatformOn) {
      // Currently on — turn off, dropping whatever custom name was saved.
      const next = enabledPlatforms.filter((p) => p !== savedOtherPlatform);
      saveSettings("platforms_enabled", JSON.stringify(next));
      setOtherPlatformOn(false);
      setOtherPlatformInput("");
    } else {
      // Turning on with no name yet — nothing to save until they type one.
      setOtherPlatformOn(true);
    }
    setUpdated(true);
  };

  const saveOtherPlatformName = () => {
    const name = otherPlatformInput.trim();
    const withoutOther = enabledPlatforms.filter(
      (p) => p !== savedOtherPlatform,
    );
    const next = name ? [...withoutOther, name] : withoutOther;
    saveSettings("platforms_enabled", JSON.stringify(next));
    setUpdated(true);
  };

  return (
    <div className="row justify-content-center mt-3">
      <div className="col-md-7">
        <div className="card">
          <div className="card-body ">
            <h3 className="">Reportes de Ventas</h3>
            <p>
              Tu sistema en Foo para Empresas te puede ayudar a saber de mejor
              manera cuánto estás ganando realmente con el uso de servicios
              adicionales.
            </p>
            <div className="form-group row mx-auto">
              <label
                htmlFor="input_terminal"
                className="col-sm-3 col-htmlForm-label"
              >
                Terminal
              </label>
              <div className="col-sm-3">
                <input
                  type="tel"
                  className="form-control service_val"
                  id="input_terminal"
                  data-service="terminal"
                  placeholder="comisión"
                  value="3.60"
                />
                <span className="feedback_input text-muted">%</span>
              </div>
            </div>
            <button type="button" className="btn btn-success btn-block mt-4">
              Guardar
            </button>
          </div>{" "}
        </div>
      </div>
      <div className="col">
        <h3 className="">Otras Opciones</h3>
        <IconButton
          clickFun={updateSetting}
          text="Cobros a un click"
          classes="btn-sm"
          checked={settings.quick_charge === true ? undefined : "true"}
          id="quick_charge"
        />
        <IconButton
          clickFun={updateSetting}
          text="Mostar tipo de tarjeta al cobrar"
          classes="btn-sm"
          checked={settings.cards_type === true ? "true" : undefined}
          id="cards_type"
        />
        <IconButton
          clickFun={updateSetting}
          text="Pantalla de bloqueo"
          classes="btn-sm"
          checked={settings.lock_screen !== false ? "true" : undefined}
          id="lock_screen"
        />
        <IconButton
          clickFun={updateSetting}
          text="Permitir múltiples órdenes por mesa"
          classes="btn-sm"
          checked={settings.multi_table_orders === true ? "true" : undefined}
          id="multi_table_orders"
        />
        <IconButton
          clickFun={updateSetting}
          text="Activar opción de venta &quot;Plataformas&quot;"
          classes="btn-sm"
          checked={settings.activate_platforms === true ? "true" : undefined}
          id="activate_platforms"
        />
        <p className="text-muted small ml-4 pl-2 mt-n1">
          Muestra la opción "Plataformas" en el punto de venta para
          registrar pedidos de Uber Eats, Rappi, Didi Food u otras apps.
        </p>
        {settings.activate_platforms ? (
          <div className="ml-4 pl-2 mb-3">
            <p className="text-muted small mb-2">
              ¿Con qué apps de reparto vendes? Selecciona todas las que
              apliquen.
            </p>
            {FIXED_PLATFORM_OPTIONS.map((platform) => (
              <div className="custom-control custom-checkbox mb-1" key={platform}>
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id={`platform_${platform}`}
                  checked={enabledPlatforms.includes(platform)}
                  onChange={() => togglePlatform(platform)}
                />
                <label
                  className="custom-control-label"
                  htmlFor={`platform_${platform}`}
                >
                  {platform}
                </label>
              </div>
            ))}
            <div className="custom-control custom-checkbox mb-1">
              <input
                type="checkbox"
                className="custom-control-input"
                id="platform_other"
                checked={otherPlatformOn}
                onChange={toggleOtherPlatform}
              />
              <label className="custom-control-label" htmlFor="platform_other">
                Otra
              </label>
            </div>
            {otherPlatformOn ? (
              <div className="input-group input-group-sm mt-1 mb-2" style={{ maxWidth: 260 }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nombre de la app"
                  value={otherPlatformInput}
                  onChange={(ev) => setOtherPlatformInput(ev.target.value)}
                  onBlur={saveOtherPlatformName}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter") {
                      ev.preventDefault();
                      saveOtherPlatformName();
                    }
                  }}
                />
              </div>
            ) : null}
          </div>
        ) : null}
        <IconButton
          clickFun={updateSetting}
          text="Cobrar pedidos de plataformas"
          classes="btn-sm"
          checked={
            settings.charge_platform_orders === true ? "true" : undefined
          }
          id="charge_platform_orders"
        />
        <p className="text-muted small ml-4 pl-2 mt-n1">
          Por defecto los pedidos de plataformas se marcan como pagados
          automáticamente porque la app le paga directamente. Activa esta
          opción si tú cobras esos pedidos.
        </p>
        <IconButton
          clickFun={() => {
            const next = !posAlertsOn;
            setPosAlertsEnabled(next);
            setPosAlertsOn(next);
          }}
          text="Notificaciones de nuevas órdenes POS"
          classes="btn-sm"
          checked={posAlertsOn ? "true" : undefined}
          id="pos_order_alerts"
        />
        <p className="text-muted small ml-4 pl-2 mt-n1">
          Los pedidos en línea siempre muestran notificación.
        </p>
        <hr />
        <h5>Agregar Cliente en POS</h5>
        <p className="text-muted small">
          Solicitar datos del cliente al crear un pedido en el punto de venta.
        </p>
        <div className="btn-group-vertical w-100" role="group">
          <button
            className={`btn btn-outline-dark text-left ${
              posAddClient === "disabled" ? "active" : ""
            }`}
            onClick={() => { saveSettings("pos_add_client", "disabled"); setUpdated(true); }}
          >
            <i
              className={`far ${
                posAddClient === "disabled"
                  ? "fa-check-circle"
                  : "fa-circle"
              } mr-2`}
            />
            No solicitar cliente
          </button>
          <button
            className={`btn btn-outline-dark text-left ${
              posAddClient === "always" ? "active" : ""
            }`}
            onClick={() => { saveSettings("pos_add_client", "always"); setUpdated(true); }}
          >
            <i
              className={`far ${
                posAddClient === "always"
                  ? "fa-check-circle"
                  : "fa-circle"
              } mr-2`}
            />
            Siempre solicitar cliente
          </button>
          <button
            className={`btn btn-outline-dark text-left ${
              posAddClient === "pickup" ? "active" : ""
            }`}
            onClick={() => { saveSettings("pos_add_client", "pickup"); setUpdated(true); }}
          >
            <i
              className={`far ${
                posAddClient === "pickup"
                  ? "fa-check-circle"
                  : "fa-circle"
              } mr-2`}
            />
            Solo en pedidos para recoger
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sales;
